import moment from "moment";
import React, { useEffect, useMemo, useState } from "react";
import { Col, FormControl, Modal, Row } from "react-bootstrap";
import { combineDateAndTime, isAdmin } from "../../helpers/global";
import useAppChoices from "../../hooks/useAppChoices";
import useAuth from "../../hooks/useAuth";
import HorizontalProgress from "../../HorizontalProgress";
import { getJobTitle } from "../../Util/jobUtil";
import AppButton from "../common/AppButton";
import CustomMultiSelect from "../CustomMultiSelect";
import JobListModal from "./JobLIstModal";

const formatValues = (values) => {
  return {
    ...values,
    startDateTime: combineDateAndTime(values?.date, values?.startTime),
    endDateTime: combineDateAndTime(values?.date, values?.endTime),
  };
};

const JobPicker = ({ job, onJobChange, disabled }) => {
  const [openJobPicker, setOpenJobPicker] = useState(false);

  const handlePickerClick = () => {
    if (!disabled) {
      setOpenJobPicker(true);
    }
  };

  return (
    <>
      <div
        className="p-1 d-flex align-items-center smallFont w-100 border rounded cursor-pointer hover-light"
        style={{ height: 30 }}
        onClick={handlePickerClick}
      >
        {job ? getJobTitle(job) : "Select A Job"}
      </div>
      <JobListModal
        show={openJobPicker}
        onHide={() => setOpenJobPicker(false)}
        onSelectJob={(job) => {
          onJobChange(job);
          setOpenJobPicker(false);
        }}
      />
    </>
  );
};

const AddEditTimeTrackerModal = ({
  show,
  onHide,
  mode,
  values = {},
  showProgress,
  onSubmit,
}) => {
  const { user } = useAuth();
  const users = useAppChoices("users");
  const [formErrors, setFormErrors] = useState(null);
  const [formValues, setFormValues] = useState(null);

  const onFormValueChange = (key, value) => {
    if (formErrors?.[key]) {
      delete formErrors[key];
      setFormErrors(formErrors);
    }
    setFormValues({ ...formValues, [key]: value });
  };

  const isRestrictedEdit =
    mode === "update" &&
    !isAdmin(user.role) &&
    values.status === "Unapproved" &&
    values.entryOrigin === "Clocked";

  const formFields = useMemo(
    () =>
      [
        {
          label: "Job",
          key: "job",
          customField: ({ formValues, onFormValueChange }) => (
            <JobPicker
              job={formValues?.job}
              onJobChange={(job) => onFormValueChange("job", job)}
              disabled={isRestrictedEdit}
            />
          ),
          required: true,
          md: 8,
          lg: 8,
          disabled: isRestrictedEdit,
        },
        {
          label: "User",
          key: "user",
          type: "dropdown",
          required: isAdmin(user?.role),
          hide: !isAdmin(user?.role),
          valueAccessor: (v) => v?.user?._id || v?.user || "",
          options: users?.map((u) => ({ label: u?.name, value: u?._id })),
          md: 4,
          lg: 4,
        },
        {
          label: "Status",
          key: "status",
          type: "dropdown",
          required: isAdmin(user?.role),
          hide: !isAdmin(user?.role) || mode === "add",
          options: ["Approved", "Unapproved"]?.map((s) => ({
            label: s,
            value: s,
          })),
          md: 3,
          lg: 3,
        },
        {
          label: "Date",
          key: "date",
          required: true,
          type: "date",
          md: mode === "add" ? 6 : 3,
          lg: mode === "add" ? 6 : 3,
          disabled: isRestrictedEdit,
        },
        {
          label: "Start time",
          key: "startTime",
          type: "time",
          required: true,
          customValidation: (values) => {
            let startDateTime = combineDateAndTime(
              values?.date,
              values?.startTime
            );
            let endDateTime = combineDateAndTime(values?.date, values?.endTime);
            let isInvalid =
              values?.endTime &&
              endDateTime.getTime() <= startDateTime.getTime();

            return {
              error: isInvalid,
              message: isInvalid
                ? "Start time should be less than end time"
                : "",
            };
          },
          md: 3,
          lg: 3,
          disabled: isRestrictedEdit,
        },
        {
          label: "End time",
          key: "endTime",
          required: true,
          type: "time",
          md: 3,
          lg: 3,
          disabled: isRestrictedEdit,
        },
        {
          label: "Comment",
          key: "comment",
          as: "textarea",
          type: "text",
        },
      ].filter((f) => !f?.hide),
    [users, user, mode, isRestrictedEdit]
  );

  const handleSubmit = () => {
    let errors = {};
    formFields
      ?.filter((f) => !f.disabled)
      .forEach(({ key, label, customValidation, required = false }) => {
        let value = formValues?.[key];
        if (required && !value) {
          errors[key] = `${label} is required`;
          return;
        }
        if (customValidation) {
          let { error = false, message = "Invalid" } = customValidation(
            formValues
          );
          if (error) {
            errors[key] = message;
            return;
          }
        }
      });
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    } else {
      setFormErrors(null);
      onSubmit(formatValues(formValues));
    }
  };

  useEffect(() => {
    if (show) {
      setFormValues({
        ...values,
        date: values?.startDateTime
          ? moment(values?.startDateTime).format("YYYY-MM-DD")
          : "",
        startTime: values?.startDateTime
          ? moment(values?.startDateTime).format("HH:mm")
          : "",
        endTime: values?.endDateTime
          ? moment(values?.endDateTime).format("HH:mm")
          : "",
      });
      setFormErrors(null);
    }
  }, [show, values]);

  return (
    <Modal centered show={show} onHide={onHide}>
      <Modal.Header closeButton className="py-1 px-2 d-flex align-items-center">
        <h6 className="mb-0">
          {mode === "update" ? "Update Time Log" : "Add time log"}
        </h6>
      </Modal.Header>
      <Modal.Body className="p-2">
        <Row className="m-0">
          {formFields
            ?.filter((f) => !f.disabled)
            .map(
              ({
                key,
                label,
                type,
                customField,
                valueAccessor,
                options,
                as,
                cols,
                xs = 12,
                sm = 12,
                md = 12,
                lg = 12,
                disabled,
              }) => {
                return (
                  <Col
                    key={key}
                    xs={xs}
                    sm={sm}
                    md={md}
                    lg={lg}
                    className="px-1 pb-3"
                  >
                    <h6 className="smallFont">{label}</h6>
                    {customField ? (
                      customField({ formValues, onFormValueChange })
                    ) : type === "dropdown" ? (
                      <CustomMultiSelect
                        selectedItems={
                          valueAccessor
                            ? valueAccessor(formValues)
                            : formValues?.[key]
                        }
                        items={options || []}
                        isMulti={false}
                        closeMenuOnSelect
                        height="30px"
                        onChange={(value) => onFormValueChange(key, value)}
                        disabled={disabled}
                      />
                    ) : (
                      <FormControl
                        size="sm"
                        type={type}
                        as={as}
                        cols={cols}
                        value={formValues?.[key] || ""}
                        onChange={(e) => onFormValueChange(key, e.target.value)}
                        disabled={disabled}
                      />
                    )}
                    <h6 className="smallFont text-danger mt-1 mb-0">
                      {formErrors?.[key]}
                    </h6>
                  </Col>
                );
              }
            )}
        </Row>
        {showProgress && (
          <HorizontalProgress
            text={mode === "add" ? "Adding..." : "Updating..."}
          />
        )}
      </Modal.Body>
      <Modal.Footer className="p-1">
        <AppButton
          disabled={showProgress}
          onClick={onHide}
          variant="danger"
          text="Close"
        />
        <AppButton
          disabled={showProgress}
          onClick={handleSubmit}
          variant="success"
          text="Save"
        />
      </Modal.Footer>
    </Modal>
  );
};

export default AddEditTimeTrackerModal;
